// Market Configuration — Localization for Scandinavian & EU markets
// ================================================================
// Maps locale codes to language strings, address formats, postal code
// validation, phone prefixes, and Surfboard SDK locale codes.
//
// Supported markets:
//   NO (Norway), SE (Sweden), DK (Denmark), FI (Finland), EN (English fallback)

export type MarketLocale = 'no' | 'sv' | 'da' | 'fi' | 'en';

export interface MarketConfig {
  /** Locale code for Surfboard SDK changeLocale() */
  sdkLocale: string;
  /** ISO 4217 default currency */
  defaultCurrency: string;
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
  /** Default phone country code (without +) */
  phonePrefix: string;
  /** Postal code regex validation */
  postalCodePattern: RegExp;
  /** Postal code max length */
  postalCodeLength: number;
  /** Postal code placeholder */
  postalCodePlaceholder: string;
  /** All UI labels */
  labels: MarketLabels;
}

export interface MarketLabels {
  // Identity step
  emailLabel: string;
  emailPlaceholder: string;
  emailRequired: string;
  emailInvalid: string;
  postalCodeLabel: string;
  postalCodeRequired: string;
  postalCodeInvalid: string;
  continueButton: string;

  // Address step
  fullNameLabel: string;
  fullNamePlaceholder: string;
  fullNameRequired: string;
  streetLabel: string;
  streetPlaceholder: string;
  streetRequired: string;
  cityLabel: string;
  cityRequired: string;
  saveAddressLabel: string;
  savedAddressTitle: string;
  newAddressButton: string;
  deleteDataButton: string;
  deleteDataConfirm: string;

  // Shipping step
  shippingTitle: string;
  shippingSubtitle: string;
  freeLabel: string;
  deliveryEstimate: string;
  pickupPointLabel: string;
  unknownDeliveryTime: string;
  digitalDelivery: string;
  noPhysicalDelivery: string;
  noShippingConfigured: string;
  noShippingAvailable: string;
  shippingFetchError: string;
  deliveryLabel: string;
  shippingNotConfiguredTitle: string;
  shippingNotConfiguredDesc: string;
  shippingErrorTitle: string;
  shippingContactStore: string;
  cheapestBadge: string;
  fastestBadge: string;
  deliveryOptionFallback: string;
  freeShippingAria: string;
  continueToPayment: string;
  fetchingShippingFor: string;
  goBackLabel: string;
  currencySymbol: string;

  // Order summary
  orderAmountLabel: string;
  inclVatShipping: string;
  hideDetails: string;
  showDetails: string;
  orderDetailsAria: string;
  shippingCostLabel: string;

  // Success step
  paymentCompleted: string;
  thankYouMessage: string;
  amountPaid: string;
  receiptSentTo: string;
  paymentMethodLabel: string;
  orderIdLabel: string;
  redirectCountdown: string;
  continueToStore: string;
  paymentRegistered: string;
  closeWindowHint: string;

  // Progress bar
  stepContact: string;
  stepAddress: string;
  stepShipping: string;
  stepPayment: string;
  goBackToStep: string;

  // Payment step
  paymentTitle: string;
  cardLabel: string;
  vippsLabel: string;
  klarnaLabel: string;
  swishLabel: string;
  mobilePayLabel: string;
  payButton: string;
  processingLabel: string;
  lastUsedLabel: string;
  selectMethodError: string;
  paymentFailed: string;
  paymentDeclined: string;
  paymentSystemError: string;
  paymentDataError: string;
  completedLabel: string;
  waitMessage: string;
  orPayWith: string;
  securePayment: string;

  // General
  backButton: string;
  loadingLabel: string;
  errorGeneric: string;
  retryButton: string;
  termsPrefix: string;
  termsIdentityPrefix: string;
  termsLink: string;
  privacyLink: string;
  andWord: string;
  byProceedingText: string;
}

// =====================================================
// MARKET CONFIGURATIONS
// =====================================================

const NO_CONFIG: MarketConfig = {
  sdkLocale: 'no',
  defaultCurrency: 'NOK',
  countryCode: 'NO',
  phonePrefix: '47',
  postalCodePattern: /^\d{4}$/,
  postalCodeLength: 4,
  postalCodePlaceholder: '0000',
  labels: {
    emailLabel: 'E-postadresse',
    emailPlaceholder: 'din@epost.no',
    emailRequired: 'E-postadresse er påkrevd',
    emailInvalid: 'Ugyldig e-postadresse',
    postalCodeLabel: 'Postnummer',
    postalCodeRequired: 'Postnummer er påkrevd',
    postalCodeInvalid: 'Postnummer må være 4 siffer',
    continueButton: 'Fortsett',

    fullNameLabel: 'Fullt navn',
    fullNamePlaceholder: 'Ola Nordmann',
    fullNameRequired: 'Navn er påkrevd',
    streetLabel: 'Gateadresse',
    streetPlaceholder: 'Storgata 1',
    streetRequired: 'Gateadresse er påkrevd',
    cityLabel: 'Poststed',
    cityRequired: 'Poststed er påkrevd',
    saveAddressLabel: 'Lagre adressen for neste gang',
    savedAddressTitle: 'Lagrede adresser',
    newAddressButton: 'Bruk ny adresse',
    deleteDataButton: 'Slett mine data',
    deleteDataConfirm: 'Er du sikker? Alle lagrede adresser slettes.',

    shippingTitle: 'Velg leveringsmetode',
    shippingSubtitle: 'Hvordan vil du motta bestillingen din?',
    freeLabel: 'Gratis',
    deliveryEstimate: 'Estimert levering',
    pickupPointLabel: 'Velg hentested',
    unknownDeliveryTime: 'Ukjent leveringstid',
    digitalDelivery: 'Digital levering',
    noPhysicalDelivery: 'Ingen fysisk levering',
    noShippingConfigured: 'Ingen fraktalternativer er konfigurert for denne butikken.',
    noShippingAvailable: 'Ingen fraktalternativer tilgjengelig for dette postnummeret.',
    shippingFetchError: 'Kunne ikke hente fraktalternativer',
    deliveryLabel: 'Levering',
    shippingNotConfiguredTitle: 'Frakt ikke konfigurert',
    shippingNotConfiguredDesc: 'Butikken har ikke satt opp fraktalternativer enda.',
    shippingErrorTitle: 'Kunne ikke hente frakt',
    shippingContactStore: 'Ta kontakt med butikken for \u00e5 fullf\u00f8re bestillingen.',
    cheapestBadge: 'Billigst',
    fastestBadge: 'Raskest',
    deliveryOptionFallback: 'Leveringsalternativ',
    freeShippingAria: 'Gratis frakt',
    continueToPayment: 'Fortsett til betaling',
    fetchingShippingFor: 'Henter fraktalternativer for',
    goBackLabel: 'G\u00e5 tilbake',
    currencySymbol: 'kr',

    orderAmountLabel: 'Ordrebel\u00f8p',
    inclVatShipping: 'Inkl. MVA & frakt',
    hideDetails: 'Skjul detaljer',
    showDetails: 'Vis detaljer',
    orderDetailsAria: 'Ordredetaljer',
    shippingCostLabel: 'Frakt',

    paymentCompleted: 'Betaling fullf\u00f8rt!',
    thankYouMessage: 'Takk for din bestilling',
    amountPaid: 'Bel\u00f8p betalt',
    receiptSentTo: 'Kvittering sendes til',
    paymentMethodLabel: 'Betalingsmetode',
    orderIdLabel: 'Ordre-ID',
    redirectCountdown: 'Du blir videresendt om',
    continueToStore: 'Fortsett til butikken',
    paymentRegistered: 'Din betaling er registrert.',
    closeWindowHint: 'Du kan n\u00e5 lukke dette vinduet.',

    stepContact: 'Kontakt',
    stepAddress: 'Adresse',
    stepShipping: 'Levering',
    stepPayment: 'Betaling',
    goBackToStep: 'G\u00e5 tilbake til',

    paymentTitle: 'Velg betalingsmetode',
    cardLabel: 'Kort',
    vippsLabel: 'Vipps',
    klarnaLabel: 'Klarna',
    swishLabel: 'Swish',
    mobilePayLabel: 'MobilePay',
    payButton: 'Betal',
    processingLabel: 'Behandler betaling...',
    lastUsedLabel: 'Sist brukt',
    selectMethodError: 'Velg en betalingsmåte',
    paymentFailed: 'Betalingen feilet',
    paymentDeclined: 'Betalingen ble avvist. Prøv et annet kort eller velg en annen betalingsmåte.',
    paymentSystemError: 'Kunne ikke laste betalingssystem',
    paymentDataError: 'Kunne ikke laste betalingsdata',
    completedLabel: 'Fullført!',
    waitMessage: 'Vennligst vent, ikke lukk vinduet',
    orPayWith: 'eller betal med',
    securePayment: '🔒 Sikker betaling med kryptering',

    backButton: 'Tilbake',
    loadingLabel: 'Laster...',
    errorGeneric: 'Noe gikk galt. Prøv igjen.',
    retryButton: 'Prøv igjen',
    termsPrefix: 'Ved å betale godtar du butikkens',
    termsIdentityPrefix: 'Ved å gå videre godtar du butikkens',
    termsLink: 'kjøpsvilkår',
    privacyLink: 'personvernerklæring',
    andWord: 'og',
    byProceedingText: 'Ved å gå videre godtar du vilkårene',
  },
};

const SE_CONFIG: MarketConfig = {
  sdkLocale: 'sv',
  defaultCurrency: 'SEK',
  countryCode: 'SE',
  phonePrefix: '46',
  postalCodePattern: /^\d{3}\s?\d{2}$|^\d{5}$/,
  postalCodeLength: 6, // 5 digits or "XXX XX" format
  postalCodePlaceholder: '123 45',
  labels: {
    emailLabel: 'E-postadress',
    emailPlaceholder: 'din@epost.se',
    emailRequired: 'E-postadress krävs',
    emailInvalid: 'Ogiltig e-postadress',
    postalCodeLabel: 'Postnummer',
    postalCodeRequired: 'Postnummer krävs',
    postalCodeInvalid: 'Postnummer måste vara 5 siffror',
    continueButton: 'Fortsätt',

    fullNameLabel: 'Fullständigt namn',
    fullNamePlaceholder: 'Sven Svensson',
    fullNameRequired: 'Namn krävs',
    streetLabel: 'Gatuadress',
    streetPlaceholder: 'Storgatan 1',
    streetRequired: 'Gatuadress krävs',
    cityLabel: 'Postort',
    cityRequired: 'Postort krävs',
    saveAddressLabel: 'Spara adressen till nästa gång',
    savedAddressTitle: 'Sparade adresser',
    newAddressButton: 'Använd ny adress',
    deleteDataButton: 'Radera mina uppgifter',
    deleteDataConfirm: 'Är du säker? Alla sparade adresser raderas.',

    shippingTitle: 'Välj leveranssätt',
    shippingSubtitle: 'Hur vill du ta emot din beställning?',
    freeLabel: 'Gratis',
    deliveryEstimate: 'Beräknad leverans',
    pickupPointLabel: 'Välj utlämningsställe',
    unknownDeliveryTime: 'Okänd leveranstid',
    digitalDelivery: 'Digital leverans',
    noPhysicalDelivery: 'Ingen fysisk leverans',
    noShippingConfigured: 'Inga fraktalternativ är konfigurerade för denna butik.',
    noShippingAvailable: 'Inga fraktalternativ tillgängliga för detta postnummer.',
    shippingFetchError: 'Kunde inte hämta fraktalternativ',
    deliveryLabel: 'Leverans',
    shippingNotConfiguredTitle: 'Frakt ej konfigurerad',
    shippingNotConfiguredDesc: 'Butiken har inte konfigurerat fraktalternativ ännu.',
    shippingErrorTitle: 'Kunde inte hämta frakt',
    shippingContactStore: 'Kontakta butiken för att slutföra beställningen.',
    cheapestBadge: 'Billigast',
    fastestBadge: 'Snabbast',
    deliveryOptionFallback: 'Leveransalternativ',
    freeShippingAria: 'Gratis frakt',
    continueToPayment: 'Fortsätt till betalning',
    fetchingShippingFor: 'Hämtar fraktalternativ för',
    goBackLabel: 'Gå tillbaka',
    currencySymbol: 'kr',

    orderAmountLabel: 'Orderbelopp',
    inclVatShipping: 'Inkl. moms & frakt',
    hideDetails: 'Dölj detaljer',
    showDetails: 'Visa detaljer',
    orderDetailsAria: 'Orderdetaljer',
    shippingCostLabel: 'Frakt',

    paymentCompleted: 'Betalning genomförd!',
    thankYouMessage: 'Tack för din beställning',
    amountPaid: 'Betalt belopp',
    receiptSentTo: 'Kvitto skickas till',
    paymentMethodLabel: 'Betalningsmetod',
    orderIdLabel: 'Order-ID',
    redirectCountdown: 'Du omdirigeras om',
    continueToStore: 'Fortsätt till butiken',
    paymentRegistered: 'Din betalning är registrerad.',
    closeWindowHint: 'Du kan nu stänga detta fönster.',

    stepContact: 'Kontakt',
    stepAddress: 'Adress',
    stepShipping: 'Leverans',
    stepPayment: 'Betalning',
    goBackToStep: 'Gå tillbaka till',

    paymentTitle: 'Välj betalningssätt',
    cardLabel: 'Kort',
    vippsLabel: 'Vipps',
    klarnaLabel: 'Klarna',
    swishLabel: 'Swish',
    mobilePayLabel: 'MobilePay',
    payButton: 'Betala',
    processingLabel: 'Behandlar betalning...',
    lastUsedLabel: 'Senast använd',
    selectMethodError: 'Välj ett betalningssätt',
    paymentFailed: 'Betalningen misslyckades',
    paymentDeclined: 'Betalningen nekades. Prova ett annat kort eller välj ett annat betalsätt.',
    paymentSystemError: 'Kunde inte ladda betalningssystemet',
    paymentDataError: 'Kunde inte ladda betalningsdata',
    completedLabel: 'Klart!',
    waitMessage: 'Vänta, stäng inte fönstret',
    orPayWith: 'eller betala med',
    securePayment: '🔒 Säker betalning med kryptering',

    backButton: 'Tillbaka',
    loadingLabel: 'Laddar...',
    errorGeneric: 'Något gick fel. Försök igen.',
    retryButton: 'Försök igen',
    termsPrefix: 'Genom att betala godkänner du butikens',
    termsIdentityPrefix: 'Genom att gå vidare godkänner du butikens',
    termsLink: 'köpvillkor',
    privacyLink: 'integritetspolicy',
    andWord: 'och',
    byProceedingText: 'Genom att gå vidare godkänner du villkoren',
  },
};

const DK_CONFIG: MarketConfig = {
  sdkLocale: 'da',
  defaultCurrency: 'DKK',
  countryCode: 'DK',
  phonePrefix: '45',
  postalCodePattern: /^\d{4}$/,
  postalCodeLength: 4,
  postalCodePlaceholder: '0000',
  labels: {
    emailLabel: 'E-mailadresse',
    emailPlaceholder: 'din@email.dk',
    emailRequired: 'E-mailadresse er påkrævet',
    emailInvalid: 'Ugyldig e-mailadresse',
    postalCodeLabel: 'Postnummer',
    postalCodeRequired: 'Postnummer er påkrævet',
    postalCodeInvalid: 'Postnummer skal være 4 cifre',
    continueButton: 'Fortsæt',

    fullNameLabel: 'Fulde navn',
    fullNamePlaceholder: 'Anders Andersen',
    fullNameRequired: 'Navn er påkrævet',
    streetLabel: 'Gadeadresse',
    streetPlaceholder: 'Storegade 1',
    streetRequired: 'Gadeadresse er påkrævet',
    cityLabel: 'By',
    cityRequired: 'By er påkrævet',
    saveAddressLabel: 'Gem adressen til næste gang',
    savedAddressTitle: 'Gemte adresser',
    newAddressButton: 'Brug ny adresse',
    deleteDataButton: 'Slet mine data',
    deleteDataConfirm: 'Er du sikker? Alle gemte adresser slettes.',

    shippingTitle: 'Vælg leveringsmetode',
    shippingSubtitle: 'Hvordan vil du modtage din bestilling?',
    freeLabel: 'Gratis',
    deliveryEstimate: 'Estimeret levering',
    pickupPointLabel: 'Vælg afhentningssted',
    unknownDeliveryTime: 'Ukendt leveringstid',
    digitalDelivery: 'Digital levering',
    noPhysicalDelivery: 'Ingen fysisk levering',
    noShippingConfigured: 'Ingen fragtmuligheder er konfigureret for denne butik.',
    noShippingAvailable: 'Ingen fragtmuligheder tilgængelige for dette postnummer.',
    shippingFetchError: 'Kunne ikke hente fragtmuligheder',
    deliveryLabel: 'Levering',
    shippingNotConfiguredTitle: 'Fragt ikke konfigureret',
    shippingNotConfiguredDesc: 'Butikken har ikke opsat fragtmuligheder endnu.',
    shippingErrorTitle: 'Kunne ikke hente fragt',
    shippingContactStore: 'Kontakt butikken for at færdiggøre bestillingen.',
    cheapestBadge: 'Billigst',
    fastestBadge: 'Hurtigst',
    deliveryOptionFallback: 'Leveringsmulighed',
    freeShippingAria: 'Gratis fragt',
    continueToPayment: 'Fortsæt til betaling',
    fetchingShippingFor: 'Henter fragtmuligheder for',
    goBackLabel: 'Gå tilbage',
    currencySymbol: 'kr',

    orderAmountLabel: 'Ordrebeløb',
    inclVatShipping: 'Inkl. moms & fragt',
    hideDetails: 'Skjul detaljer',
    showDetails: 'Vis detaljer',
    orderDetailsAria: 'Ordredetaljer',
    shippingCostLabel: 'Fragt',

    paymentCompleted: 'Betaling gennemført!',
    thankYouMessage: 'Tak for din bestilling',
    amountPaid: 'Betalt beløb',
    receiptSentTo: 'Kvittering sendes til',
    paymentMethodLabel: 'Betalingsmetode',
    orderIdLabel: 'Ordre-ID',
    redirectCountdown: 'Du omdirigeres om',
    continueToStore: 'Fortsæt til butikken',
    paymentRegistered: 'Din betaling er registreret.',
    closeWindowHint: 'Du kan nu lukke dette vindue.',

    stepContact: 'Kontakt',
    stepAddress: 'Adresse',
    stepShipping: 'Levering',
    stepPayment: 'Betaling',
    goBackToStep: 'Gå tilbage til',

    paymentTitle: 'Vælg betalingsmetode',
    cardLabel: 'Kort',
    vippsLabel: 'Vipps',
    klarnaLabel: 'Klarna',
    swishLabel: 'Swish',
    mobilePayLabel: 'MobilePay',
    payButton: 'Betal',
    processingLabel: 'Behandler betaling...',
    lastUsedLabel: 'Sidst brugt',
    selectMethodError: 'Vælg en betalingsmetode',
    paymentFailed: 'Betalingen mislykkedes',
    paymentDeclined: 'Betalingen blev afvist. Prøv et andet kort eller vælg en anden betalingsmetode.',
    paymentSystemError: 'Kunne ikke indlæse betalingssystemet',
    paymentDataError: 'Kunne ikke indlæse betalingsdata',
    completedLabel: 'Færdig!',
    waitMessage: 'Vent venligst, luk ikke vinduet',
    orPayWith: 'eller betal med',
    securePayment: '🔒 Sikker betaling med kryptering',

    backButton: 'Tilbage',
    loadingLabel: 'Indlæser...',
    errorGeneric: 'Noget gik galt. Prøv igen.',
    retryButton: 'Prøv igen',
    termsPrefix: 'Ved at betale accepterer du butikkens',
    termsIdentityPrefix: 'Ved at gå videre accepterer du butikkens',
    termsLink: 'handelsbetingelser',
    privacyLink: 'privatlivspolitik',
    andWord: 'og',
    byProceedingText: 'Ved at gå videre accepterer du vilkårene',
  },
};

const FI_CONFIG: MarketConfig = {
  sdkLocale: 'fi',
  defaultCurrency: 'EUR',
  countryCode: 'FI',
  phonePrefix: '358',
  postalCodePattern: /^\d{5}$/,
  postalCodeLength: 5,
  postalCodePlaceholder: '00100',
  labels: {
    emailLabel: 'Sähköpostiosoite',
    emailPlaceholder: 'nimi@sahkoposti.fi',
    emailRequired: 'Sähköpostiosoite vaaditaan',
    emailInvalid: 'Virheellinen sähköpostiosoite',
    postalCodeLabel: 'Postinumero',
    postalCodeRequired: 'Postinumero vaaditaan',
    postalCodeInvalid: 'Postinumeron on oltava 5 numeroa',
    continueButton: 'Jatka',

    fullNameLabel: 'Koko nimi',
    fullNamePlaceholder: 'Matti Meikäläinen',
    fullNameRequired: 'Nimi vaaditaan',
    streetLabel: 'Katuosoite',
    streetPlaceholder: 'Mannerheimintie 1',
    streetRequired: 'Katuosoite vaaditaan',
    cityLabel: 'Kaupunki',
    cityRequired: 'Kaupunki vaaditaan',
    saveAddressLabel: 'Tallenna osoite seuraavaa kertaa varten',
    savedAddressTitle: 'Tallennetut osoitteet',
    newAddressButton: 'Käytä uutta osoitetta',
    deleteDataButton: 'Poista tietoni',
    deleteDataConfirm: 'Oletko varma? Kaikki tallennetut osoitteet poistetaan.',

    shippingTitle: 'Valitse toimitustapa',
    shippingSubtitle: 'Miten haluat vastaanottaa tilauksesi?',
    freeLabel: 'Ilmainen',
    deliveryEstimate: 'Arvioitu toimitus',
    pickupPointLabel: 'Valitse noutopiste',
    unknownDeliveryTime: 'Tuntematon toimitusaika',
    digitalDelivery: 'Digitaalinen toimitus',
    noPhysicalDelivery: 'Ei fyysist\u00e4 toimitusta',
    noShippingConfigured: 'T\u00e4lle kaupalle ei ole m\u00e4\u00e4ritetty toimitusvaihtoehtoja.',
    noShippingAvailable: 'T\u00e4lle postinumerolle ei ole toimitusvaihtoehtoja.',
    shippingFetchError: 'Toimitusvaihtoehtoja ei voitu hakea',
    deliveryLabel: 'Toimitus',
    shippingNotConfiguredTitle: 'Toimitusta ei m\u00e4\u00e4ritetty',
    shippingNotConfiguredDesc: 'Kauppa ei ole viel\u00e4 m\u00e4\u00e4ritt\u00e4nyt toimitusvaihtoehtoja.',
    shippingErrorTitle: 'Toimitusvaihtoehtoja ei voitu hakea',
    shippingContactStore: 'Ota yhteytt\u00e4 kauppaan tilauksen viimeistelemiseksi.',
    cheapestBadge: 'Halvin',
    fastestBadge: 'Nopein',
    deliveryOptionFallback: 'Toimitusvaihtoehto',
    freeShippingAria: 'Ilmainen toimitus',
    continueToPayment: 'Jatka maksuun',
    fetchingShippingFor: 'Haetaan toimitusvaihtoehtoja postinumerolle',
    goBackLabel: 'Palaa takaisin',
    currencySymbol: '\u20ac',

    orderAmountLabel: 'Tilauksen summa',
    inclVatShipping: 'Sis. ALV & toimitus',
    hideDetails: 'Piilota tiedot',
    showDetails: 'N\u00e4yt\u00e4 tiedot',
    orderDetailsAria: 'Tilaustiedot',
    shippingCostLabel: 'Toimitus',

    paymentCompleted: 'Maksu suoritettu!',
    thankYouMessage: 'Kiitos tilauksestasi',
    amountPaid: 'Maksettu summa',
    receiptSentTo: 'Kuitti l\u00e4hetet\u00e4\u00e4n osoitteeseen',
    paymentMethodLabel: 'Maksutapa',
    orderIdLabel: 'Tilaus-ID',
    redirectCountdown: 'Sinut ohjataan uudelleen',
    continueToStore: 'Jatka kauppaan',
    paymentRegistered: 'Maksusi on rekister\u00f6ity.',
    closeWindowHint: 'Voit nyt sulkea t\u00e4m\u00e4n ikkunan.',

    stepContact: 'Yhteystiedot',
    stepAddress: 'Osoite',
    stepShipping: 'Toimitus',
    stepPayment: 'Maksu',
    goBackToStep: 'Palaa kohtaan',

    paymentTitle: 'Valitse maksutapa',
    cardLabel: 'Kortti',
    vippsLabel: 'Vipps',
    klarnaLabel: 'Klarna',
    swishLabel: 'Swish',
    mobilePayLabel: 'MobilePay',
    payButton: 'Maksa',
    processingLabel: 'Käsitellään maksua...',
    lastUsedLabel: 'Viimeksi käytetty',
    selectMethodError: 'Valitse maksutapa',
    paymentFailed: 'Maksu epäonnistui',
    paymentDeclined: 'Maksu hylättiin. Kokeile toista korttia tai valitse toinen maksutapa.',
    paymentSystemError: 'Maksujärjestelmää ei voitu ladata',
    paymentDataError: 'Maksutietoja ei voitu ladata',
    completedLabel: 'Valmis!',
    waitMessage: 'Odota, älä sulje ikkunaa',
    orPayWith: 'tai maksa',
    securePayment: '🔒 Turvallinen salattu maksu',

    backButton: 'Takaisin',
    loadingLabel: 'Ladataan...',
    errorGeneric: 'Jokin meni pieleen. Yritä uudelleen.',
    retryButton: 'Yritä uudelleen',
    termsPrefix: 'Maksamalla hyväksyt kaupan',
    termsIdentityPrefix: 'Jatkamalla hyväksyt kaupan',
    termsLink: 'ehdot',
    privacyLink: 'tietosuojakäytännön',
    andWord: 'ja',
    byProceedingText: 'Jatkamalla hyväksyt ehdot',
  },
};

const EN_CONFIG: MarketConfig = {
  sdkLocale: 'en',
  defaultCurrency: 'EUR',
  countryCode: 'NO',
  phonePrefix: '47',
  postalCodePattern: /^\d{4,5}$/,
  postalCodeLength: 5,
  postalCodePlaceholder: '0000',
  labels: {
    emailLabel: 'Email address',
    emailPlaceholder: 'your@email.com',
    emailRequired: 'Email address is required',
    emailInvalid: 'Invalid email address',
    postalCodeLabel: 'Postal code',
    postalCodeRequired: 'Postal code is required',
    postalCodeInvalid: 'Invalid postal code',
    continueButton: 'Continue',

    fullNameLabel: 'Full name',
    fullNamePlaceholder: 'John Doe',
    fullNameRequired: 'Name is required',
    streetLabel: 'Street address',
    streetPlaceholder: '123 Main Street',
    streetRequired: 'Street address is required',
    cityLabel: 'City',
    cityRequired: 'City is required',
    saveAddressLabel: 'Save address for next time',
    savedAddressTitle: 'Saved addresses',
    newAddressButton: 'Use new address',
    deleteDataButton: 'Delete my data',
    deleteDataConfirm: 'Are you sure? All saved addresses will be deleted.',

    shippingTitle: 'Select delivery method',
    shippingSubtitle: 'How would you like to receive your order?',
    freeLabel: 'Free',
    deliveryEstimate: 'Estimated delivery',
    pickupPointLabel: 'Select pickup point',
    unknownDeliveryTime: 'Unknown delivery time',
    digitalDelivery: 'Digital delivery',
    noPhysicalDelivery: 'No physical delivery',
    noShippingConfigured: 'No shipping options are configured for this store.',
    noShippingAvailable: 'No shipping options available for this postal code.',
    shippingFetchError: 'Could not fetch shipping options',
    deliveryLabel: 'Delivery',
    shippingNotConfiguredTitle: 'Shipping not configured',
    shippingNotConfiguredDesc: 'The store has not set up shipping options yet.',
    shippingErrorTitle: 'Could not fetch shipping options',
    shippingContactStore: 'Contact the store to complete your order.',
    cheapestBadge: 'Cheapest',
    fastestBadge: 'Fastest',
    deliveryOptionFallback: 'Delivery option',
    freeShippingAria: 'Free shipping',
    continueToPayment: 'Continue to payment',
    fetchingShippingFor: 'Fetching shipping options for',
    goBackLabel: 'Go back',
    currencySymbol: '',

    orderAmountLabel: 'Order total',
    inclVatShipping: 'Incl. VAT & shipping',
    hideDetails: 'Hide details',
    showDetails: 'Show details',
    orderDetailsAria: 'Order details',
    shippingCostLabel: 'Shipping',

    paymentCompleted: 'Payment completed!',
    thankYouMessage: 'Thank you for your order',
    amountPaid: 'Amount paid',
    receiptSentTo: 'Receipt will be sent to',
    paymentMethodLabel: 'Payment method',
    orderIdLabel: 'Order ID',
    redirectCountdown: 'You will be redirected in',
    continueToStore: 'Continue to store',
    paymentRegistered: 'Your payment has been registered.',
    closeWindowHint: 'You can now close this window.',

    stepContact: 'Contact',
    stepAddress: 'Address',
    stepShipping: 'Shipping',
    stepPayment: 'Payment',
    goBackToStep: 'Go back to',

    paymentTitle: 'Select payment method',
    cardLabel: 'Card',
    vippsLabel: 'Vipps',
    klarnaLabel: 'Klarna',
    swishLabel: 'Swish',
    mobilePayLabel: 'MobilePay',
    payButton: 'Pay',
    processingLabel: 'Processing payment...',
    lastUsedLabel: 'Last used',
    selectMethodError: 'Select a payment method',
    paymentFailed: 'Payment failed',
    paymentDeclined: 'The payment was declined. Try another card or choose a different payment method.',
    paymentSystemError: 'Could not load payment system',
    paymentDataError: 'Could not load payment data',
    completedLabel: 'Completed!',
    waitMessage: 'Please wait, do not close the window',
    orPayWith: 'or pay with',
    securePayment: '🔒 Secure encrypted payment',

    backButton: 'Back',
    loadingLabel: 'Loading...',
    errorGeneric: 'Something went wrong. Please try again.',
    retryButton: 'Try again',
    termsPrefix: 'By paying you agree to the store\'s',
    termsIdentityPrefix: 'By proceeding you agree to the store\'s',
    termsLink: 'terms and conditions',
    privacyLink: 'privacy policy',
    andWord: 'and',
    byProceedingText: 'By proceeding you agree to the terms',
  },
};

// =====================================================
// MARKET REGISTRY
// =====================================================

const MARKET_MAP: Record<string, MarketConfig> = {
  // Locale codes (from session)
  'no': NO_CONFIG,
  'nb': NO_CONFIG,
  'nn': NO_CONFIG,
  'nb-NO': NO_CONFIG,
  'sv': SE_CONFIG,
  'sv-SE': SE_CONFIG,
  'da': DK_CONFIG,
  'da-DK': DK_CONFIG,
  'fi': FI_CONFIG,
  'fi-FI': FI_CONFIG,
  'en': EN_CONFIG,
  'en-US': EN_CONFIG,
  'en-GB': EN_CONFIG,

  // Country codes (uppercase, for convenience)
  'NO': NO_CONFIG,
  'SE': SE_CONFIG,
  'DK': DK_CONFIG,
  'FI': FI_CONFIG,
};

/**
 * Get market configuration for a given locale or country code.
 * Falls back to Norwegian if not found.
 */
export function getMarketConfig(locale?: string | null): MarketConfig {
  if (!locale) return NO_CONFIG;
  return MARKET_MAP[locale] || MARKET_MAP[locale.toLowerCase()] || NO_CONFIG;
}

/**
 * Get just the labels for a given locale.
 */
export function getLabels(locale?: string | null): MarketLabels {
  return getMarketConfig(locale).labels;
}
